import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Building2, CalendarDays, CheckCircle2, FileText, Globe2,
  HeartHandshake, Home, MessageCircle, Phone, ReceiptText, ShieldCheck,
  Sparkles, Users, WalletCards,
} from 'lucide-react';
import Seo from '@/components/Seo';

const LOGO_URL = 'https://customer-assets-wrfwihn1.emergentagent.net/job_aac32766-5fa5-4a83-81c9-03bafeaebc9e/artifacts/bi1rz20k_loyerbook.png';

const ABOUT_IMAGES = [
  'https://customer-assets-v7afamib.emergentagent.net/job_rent-manager-dev/artifacts/7s1xtz4a_Sobrado%20de%20tijolinhos.jfif',
  'https://customer-assets-v7afamib.emergentagent.net/job_rent-manager-dev/artifacts/kqyjvt2a_t%C3%A9l%C3%A9charger%20%2814%29.jfif',
  'https://customer-assets-v7afamib.emergentagent.net/job_rent-manager-dev/artifacts/spgml1vh_t%C3%A9l%C3%A9charger%20%2815%29.jfif',
  'https://customer-assets-v7afamib.emergentagent.net/job_rent-manager-dev/artifacts/0n2y3ln2_t%C3%A9l%C3%A9charger%20%2816%29.jfif',
];

const VALUES = [
  { icon: ShieldCheck, title: 'Confiance', text: 'Donner aux bailleurs et aux locataires un historique clair, lisible et traçable de la gestion locative.' },
  { icon: Sparkles, title: 'Simplicité', text: 'Réduire la complexité administrative avec une interface directe, pensée pour le web et le mobile.' },
  { icon: HeartHandshake, title: 'Proximité', text: 'Adapter LoyerBook aux réalités locales : pays, indicatifs, devises et tarification commerciale par marché.' },
];

const HOW_IT_WORKS = [
  { icon: Globe2, title: '1. Choisissez votre pays', text: 'LoyerBook adapte l’indicatif, la devise et le prix d’abonnement au marché sélectionné.' },
  { icon: Users, title: '2. Ajoutez vos locataires', text: 'Centralisez vos locataires sans quota artificiel et gardez leurs informations dans un seul espace.' },
  { icon: CalendarDays, title: '3. Suivez les échéances', text: 'Visualisez les loyers attendus, payés, partiels ou en retard et gardez une vue claire sur votre portefeuille.' },
  { icon: ReceiptText, title: '4. Gardez des preuves', text: 'Enregistrez les paiements, consultez l’historique et générez des reçus PDF professionnels.' },
];

export default function About() {
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setImageIndex((index) => (index + 1) % ABOUT_IMAGES.length), 4500);
    return () => clearInterval(timer);
  }, []);

  const schema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        '@id': 'https://loyerbook.com/a-propos#page',
        url: 'https://loyerbook.com/a-propos',
        name: 'À propos de LoyerBook',
        description: 'LoyerBook est une plateforme internationale de gestion locative qui aide les bailleurs à suivre locataires, loyers, paiements, échéances et reçus.',
        inLanguage: 'fr',
        isPartOf: { '@id': 'https://loyerbook.com/#website' },
        about: { '@id': 'https://loyerbook.com/#organization' },
      },
      {
        '@type': 'WebSite',
        '@id': 'https://loyerbook.com/#website',
        url: 'https://loyerbook.com/',
        name: 'LoyerBook',
        inLanguage: 'fr',
        publisher: { '@id': 'https://loyerbook.com/#organization' },
      },
      {
        '@type': 'Organization',
        '@id': 'https://loyerbook.com/#organization',
        name: 'LoyerBook',
        url: 'https://loyerbook.com',
        logo: LOGO_URL,
        email: 'aide@loyerbook.com',
        areaServed: 'Worldwide',
        contactPoint: [
          { '@type': 'ContactPoint', telephone: '+243856776942', contactType: 'customer support', availableLanguage: ['French'] },
          { '@type': 'ContactPoint', telephone: '+243981388707', contactType: 'customer support', availableLanguage: ['French'] },
        ],
      },
      {
        '@type': 'SoftwareApplication',
        name: 'LoyerBook',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: 'https://loyerbook.com',
        description: 'Application de gestion locative pour centraliser locataires, loyers, échéances, paiements et reçus.',
        publisher: { '@id': 'https://loyerbook.com/#organization' },
      },
    ],
  }), []);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Seo
        title="À propos de LoyerBook | Gestion locative internationale"
        description="Découvrez LoyerBook, le carnet de loyer numérique pour gérer locataires, loyers, paiements, échéances et reçus dans plusieurs pays et devises."
        path="/a-propos"
        imageAlt="LoyerBook, plateforme internationale de gestion locative"
        keywords={[
          'LoyerBook', 'gestion locative', 'logiciel gestion locative', 'application bailleur',
          'suivi des loyers', 'gestion des locataires', 'reçu de loyer PDF', 'gestion immobilière',
          'carnet de loyer numérique', 'gestion locative internationale',
        ]}
        schema={schema}
      />

      <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo LoyerBook" className="h-11 w-11" />
            <div>
              <div className="text-xl font-black">Loyer<span className="text-[#1677FF]">Book</span></div>
              <div className="text-[10px] text-slate-500">Le carnet de loyer numérique</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden text-sm font-semibold text-slate-600 hover:text-[#1677FF] sm:block">Accueil</Link>
            <Link to="/inscription" className="rounded-xl bg-[#1677FF] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#0E62DB]">Créer mon compte</Link>
          </div>
        </div>
      </header>

      <main>
        <section className="overflow-hidden bg-gradient-to-br from-[#EAF3FF] via-white to-blue-50">
          <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 lg:grid-cols-[1.05fr_.95fr] lg:items-center lg:py-24">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-blue-700">
                <Globe2 className="h-4 w-4" /> À propos de LoyerBook
              </div>
              <h1 className="mt-6 text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
                La gestion locative, <span className="text-[#1677FF]">sans la paperasse qui vous ralentit.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
                LoyerBook est un carnet de loyer numérique conçu pour aider les bailleurs à centraliser leurs locataires, échéances, paiements, reçus et charges locatives dans un seul espace. La plateforme est pensée pour fonctionner pays par pays, avec indicatif, devise et tarification locale adaptés au marché sélectionné.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/inscription" className="inline-flex items-center gap-2 rounded-xl bg-[#1677FF] px-5 py-3 font-bold text-white hover:bg-[#0E62DB]">
                  Commencer avec LoyerBook <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/#pays" className="rounded-xl border border-slate-300 bg-white px-5 py-3 font-bold text-slate-700 hover:border-blue-300 hover:text-blue-600">Voir les pays</Link>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="relative h-[520px] overflow-hidden rounded-[2rem] shadow-2xl shadow-blue-900/15">
                {ABOUT_IMAGES.map((src, index) => (
                  <img
                    key={src}
                    src={src}
                    alt="Exemple de bien immobilier pouvant être suivi avec LoyerBook"
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${index === imageIndex ? 'opacity-100' : 'opacity-0'}`}
                  />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/30 bg-white/90 p-4 backdrop-blur">
                  <div className="text-xs font-bold uppercase tracking-widest text-blue-600">Votre portefeuille, au même endroit</div>
                  <div className="mt-1 text-lg font-black text-slate-900">Des appartements, des maisons, plusieurs locataires : une seule vue de gestion.</div>
                </div>
                <div className="absolute right-5 top-5 flex gap-1.5">
                  {ABOUT_IMAGES.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      aria-label={`Afficher l’image ${index + 1}`}
                      onClick={() => setImageIndex(index)}
                      className={`h-2 rounded-full transition-all ${index === imageIndex ? 'w-7 bg-white' : 'w-2 bg-white/55 hover:bg-white/80'}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <div className="grid gap-10 lg:grid-cols-[.9fr_1.1fr] lg:items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Pourquoi LoyerBook existe</div>
              <h2 className="mt-3 text-3xl font-black sm:text-4xl">Remplacer les cahiers dispersés par une gestion claire.</h2>
              <p className="mt-5 leading-relaxed text-slate-600">
                Dans beaucoup de portefeuilles locatifs, les informations sont encore réparties entre cahiers, messages, feuilles de calcul et mémoire personnelle. Résultat : échéances oubliées, historiques incomplets, reçus difficiles à retrouver et manque de visibilité sur les paiements.
              </p>
              <p className="mt-4 leading-relaxed text-slate-600">
                LoyerBook rassemble ces opérations dans un même environnement afin que le bailleur sache qui doit payer, qui a payé, ce qui reste dû et quels documents ont déjà été générés.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                [Building2, 'Portefeuille centralisé', 'Un même espace pour suivre vos biens, locataires et opérations locatives.'],
                [WalletCards, 'Paiements traçables', 'Enregistrez les paiements et conservez un historique consultable.'],
                [FileText, 'Documents utiles', 'Générez des reçus PDF et gardez les preuves associées aux paiements.'],
                [Home, 'Pensé pour les bailleurs', 'Une expérience centrée sur les besoins quotidiens de gestion locative.'],
              ].map(([Icon, title, text]) => (
                <article key={title} className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-100 text-blue-600"><Icon className="h-5 w-5" /></div>
                  <h3 className="mt-4 font-black">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-slate-100 bg-slate-50 py-20">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Comment ça fonctionne</div>
              <h2 className="mt-3 text-3xl font-black sm:text-4xl">De votre pays jusqu’au reçu de loyer.</h2>
              <p className="mt-4 text-slate-600">Le parcours est volontairement simple : configurez votre environnement, ajoutez vos locataires et commencez à suivre les opérations.</p>
            </div>
            <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {HOW_IT_WORKS.map(({ icon: Icon, title, text }) => (
                <article key={title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Icon className="h-5 w-5" /></div>
                  <h3 className="mt-4 font-black">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div className="rounded-[2rem] bg-slate-950 p-8 text-white sm:p-10">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-300"><Globe2 className="h-4 w-4" /> International par conception</div>
              <h2 className="mt-4 text-3xl font-black sm:text-4xl">Un même produit, adapté à chaque marché.</h2>
              <p className="mt-5 leading-relaxed text-slate-300">
                LoyerBook n’est plus limité à un seul pays. Le catalogue gère des marchés en Afrique, en Europe, dans les Amériques, au Moyen-Orient et en Asie, avec indicatifs téléphoniques, devises et prix locaux configurés par pays.
              </p>
              <Link to="/#pays" className="mt-7 inline-flex items-center gap-2 font-bold text-blue-300 hover:text-white">Explorer les pays <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Ce que la plateforme facilite</div>
              <h2 className="mt-3 text-3xl font-black sm:text-4xl">Moins d’incertitude dans le suivi quotidien.</h2>
              <ul className="mt-7 grid gap-4 sm:grid-cols-2">
                {[
                  'Locataires illimités', 'Suivi des loyers et échéances', 'Paiements et historique',
                  'Reçus PDF professionnels', 'Factures eau et électricité', 'Accès web et mobile',
                  'Tarification locale par pays', 'Abonnement mensuel unique',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 rounded-xl border border-slate-200 p-4 text-sm text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="bg-blue-50/70 py-20">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Nos principes</div>
              <h2 className="mt-3 text-3xl font-black sm:text-4xl">Simple à utiliser, sérieux dans la gestion.</h2>
            </div>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {VALUES.map(({ icon: Icon, title, text }) => (
                <article key={title} className="rounded-3xl border border-blue-100 bg-white p-7 shadow-sm">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-[#1677FF]"><Icon className="h-6 w-6" /></div>
                  <h3 className="mt-5 text-xl font-black">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-50">
          <div className="mx-auto max-w-5xl px-5 py-16 sm:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-black">Besoin d’aide ou d’informations ?</h2>
              <p className="mt-3 text-slate-600">Contactez LoyerBook par e-mail, appel ou WhatsApp.</p>
              <a href="mailto:aide@loyerbook.com" className="mt-3 inline-block font-bold text-blue-600 hover:underline">aide@loyerbook.com</a>
            </div>
            <div className="mx-auto mt-8 grid max-w-3xl gap-4 sm:grid-cols-2">
              {[
                { display: '+243 856 776 942', value: '243856776942' },
                { display: '+243 981 388 707', value: '243981388707' },
              ].map((phone) => (
                <div key={phone.value} className="rounded-2xl border border-slate-200 bg-white p-5 text-center shadow-sm">
                  <div className="text-lg font-black text-slate-900">{phone.display}</div>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    <a href={`tel:+${phone.value}`} className="inline-flex items-center gap-2 rounded-xl bg-[#1677FF] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#0E62DB]">
                      <Phone className="h-4 w-4" /> Appeler
                    </a>
                    <a href={`https://wa.me/${phone.value}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700">
                      <MessageCircle className="h-4 w-4" /> WhatsApp
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-5 py-20 text-center sm:px-8">
          <h2 className="text-3xl font-black sm:text-4xl">Prêt à centraliser votre gestion locative ?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-600">Choisissez votre pays, créez votre espace bailleur et retrouvez votre portefeuille dans un seul LoyerBook.</p>
          <Link to="/inscription" className="mt-8 inline-flex items-center gap-2 rounded-xl bg-[#1677FF] px-6 py-3.5 font-bold text-white hover:bg-[#0E62DB]">
            Créer mon compte <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </main>

      <footer className="border-t border-slate-100 bg-slate-50">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-5 py-6 text-sm text-slate-500 sm:flex-row sm:px-8">
          <div>© {new Date().getFullYear()} LoyerBook. Tous droits réservés.</div>
          <div className="flex gap-5">
            <Link to="/">Accueil</Link>
            <Link to="/cgu">CGU</Link>
            <Link to="/politique-confidentialite">Confidentialité</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
